import React from 'react';

import { StepProps, track } from '@betterment/js-runtime';
import { Box, Text } from '@betterment/samba-react';
import { Button, ErrorText, FormikDropdown, Heading, InputGroup } from '@betterment/style-closet-react';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { CustomerSignupValues } from './Controller';

import SecureInfoBadge from '../Shared/components/SecureInfoBadge';
import { CheckingSignupData, CheckingSignupDataParams } from '../Shared/data';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';

interface Props extends StepProps<CustomerSignupValues> {
  onSubmit: (values: CheckingSignupDataParams) => Promise<void>;
  checkingSignupData: CheckingSignupData;
  handleClose: () => void;
}

const FormSchema = Yup.object().shape<Partial<{}>>({
  value: Yup.string().required('Source of funds cannot be blank'),
});

const SourceOfFundsStep: React.FC<React.PropsWithChildren<Props>> = ({
  flowState,
  goToNextStep,
  goToPreviousStep,
  onSubmit,
  checkingSignupData,
  handleClose,
}) => {
  const { stepTitle, sourceOfFunds } = flowState.values;

  const handleSubmit = async (value: string) => {
    track('ElementClicked', { name: 'Continue', flow: 'OpenCheckingAccount' });
    await onSubmit({
      fundingSourceOption: value,
      ...(flowState.values.role && { roleOption: flowState.values.role }),
    });
    goToNextStep({ sourceOfFunds: value });
  };

  const items = (checkingSignupData.fundingSourceOptions || []).map(v => ({ value: v, text: v }));

  return (
    <WorkflowLayout
      feature="checking_customer_signup"
      pageTitle="Sign up - Checking"
      workflowTitle={stepTitle}
      onBack={goToPreviousStep}
      onClose={handleClose}
    >
      <Heading variant="primary">How do you want to fund your new account?</Heading>
      <Box
        css={{
          my: 'space.200',
        }}
      >
        <Text
          as="p"
          color="text"
          css={{
            marginBottom: 'space.300',
          }}
        >
          This info enables us to identify abnormal (and potentially fraudulent) transactions in your account.
        </Text>
      </Box>
      <Formik
        onSubmit={({ value }) => handleSubmit(value)}
        validationSchema={FormSchema}
        initialValues={{ value: sourceOfFunds || '' }}
        validateOnBlur={false}
        enableReinitialize={true}
      >
        {({ isSubmitting }) => (
          <Form>
            <InputGroup>
              <FormikDropdown
                name="value"
                id="value"
                labelText="Source of funds"
                items={items}
                placeholder="Select..."
              />
              <ErrorMessage name="value" component={ErrorText} />
            </InputGroup>
            <Button type="submit" disabled={isSubmitting}>
              Continue
            </Button>
            <Box
              css={{
                py: 'space.100',
              }}
            >
              <SecureInfoBadge />
            </Box>
          </Form>
        )}
      </Formik>
    </WorkflowLayout>
  );
};

export default SourceOfFundsStep;
